<template>
  <div
    class="images"
    :class="[
      $blockClass(blockClass, 'image-container'),
      { 'images--no-padding': !padding },
    ]"
  >
    <span
      v-if="padding"
      class="images__spacer"
      :class="[
        $blockClass(blockClass, 'spacer'),
        { 'images__spacer--mobile': ratioDesktop }
      ]"
      :style="{ paddingBlockEnd: getAspectRatio() }"
      aria-hidden="true"
    />

    <span
      v-if="padding && ratioDesktop"
      class="images__spacer images__spacer--desktop"
      :class="$blockClass(blockClass, 'spacer')"
      :style="{ paddingBlockEnd: getAspectRatio(true) }"
      aria-hidden="true"
    />

    <picture
      v-if="sources.length"
      class="images__picture"
      :class="$blockClass(blockClass, 'picture')"
    >
      <source
        v-for="(source) in sources"
        :key="`source-${source.breakpoint}`"
        class="images__image"
        :class="$blockClass(blockClass, 'image')"
        :media="source.media"
        :data-srcset="source.src"
      >

      <img
        v-if="image.src || image.url"
        class="images__image"
        :class="[
          $blockClass(blockClass, 'image'),
          { 'lazyload': lazyload },
        ]"
        :alt="imageAlt"
        decoding="async"
        :role="role"
        :src="getFallbackSrc(true)"
        :data-src="getFallbackSrc(false)"
      >
    </picture>
  </div>
</template>

<script>

/**
 * Vue: Art direction
 * -----------------------------------------------------------------------------
 * Base template for art direction image.
 * - Designed for images which change based on breakpoint.
 * - Stylesheet imported in responsive image component.
 * - image.src comes from formatted product response.
 * - image.url comes from unformatted cart.js response.
 *
 * @param {String} [alt] - Override image alt.
 * @param {String} [blockClass] - Block level class to apply to all elements.
 * @param {Boolean} [lazyload] - Lazyload image, defaults to true.
 * @param {String} [breakpointSizes] - Sizes to use at each breakpoint.
 * @param {Object} image - Image object formatted from Shopify response.
 * @param {Object} [imageDesktop] - Override image object for desktop.
 * @param {Boolean} [padding] - Add padding based on image ratio or supplied
 * ratio, default to true, disable to make image fill its parent container.
 * @param {String} [ratio] - Aspect ratio of image in `width:height` format or
 * in decimal ratio format (width/height), will use original ratio of image if
 * not set.
 * @param {String} [ratioDesktop] - Override image ratio for desktop.
 *
 */
import { values as breakpoints } from '~/config/breakpoints'
import { getSizedImageUrl } from '~/helpers/format'

export default {
  name: 'ArtDirection',

  props: {
    alt: {
      type: String,
      default: '',
    },
    blockClass: {
      type: String,
      default: '',
    },
    breakpointSizes: {
      type: [Boolean, String],
      default: false,
    },
    image: {
      type: Object,
      default: () => ({}),
      required: true,
    },
    imageDesktop: {
      type: Object,
      default: () => ({}),
    },
    lazyload: {
      type: Boolean,
      default: true,
    },
    padding: {
      type: Boolean,
      default: true,
    },
    ratio: {
      type: String,
      default: '',
    },
    ratioDesktop: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      global: true,
    }
  },

  computed: {

    /**
     * Compute alt.
     * @returns {String}
     */
    imageAlt() {
      const imageAlt = this.alt || this.image.alt
      return imageAlt ? imageAlt : ''
    },

    /**
     * Compute role.
     * @returns {String|Null}
     */
    role() {
      if (this.imageAlt) {
        return null
      }

      return 'presentation'
    },

    /**
     * Compute images array for <source> elements.
     * @returns {Array}
     */
    sources() {
      const mobile = [
        'xs',
        's',
        'm',
      ]

      return Object.keys(breakpoints).map((key, index) => {
        let imageObject = this.image

        const mediaQuerySize = breakpoints[key]

        const size = this.breakpointSizes
          ? this.breakpointSizes.split('-')[index]
          : mediaQuerySize

        if (
          (this.imageDesktop.src || this.imageDesktop.url) &&
          !mobile.includes(key)
        ) {
          imageObject = this.imageDesktop
        }

        if (!imageObject.src && !imageObject.url) {
          return false
        }

        return {
          breakpoint: key,
          media: index === (Object.keys(breakpoints).length - 1)
            ? ''
            : `(max-width: ${mediaQuerySize}px)`,
          size,
          src: getSizedImageUrl(imageObject.src || imageObject.url, `${size}x`),
        }
      }).filter(Boolean)
    },
  },

  methods: {

    /**
     * Get aspect ratio.
     * @param {Boolean} desktop - Desktop breakpoint?
     * @returns {Number|Null}
     */
    getAspectRatio(desktop) {
      if (!this.padding) {
        return null
      }

      const ratio = desktop ? this.ratioDesktop : this.ratio

      const imageObject = (this.imageDesktop.src || this.imageDesktop.url)
        ? this.imageDesktop
        : this.image

      /**
       * If ratio is supplied as a decimal ratio.
       */
      if (ratio && ratio.includes('.')) {
        return `${(1 / ratio) * 100.00}%`
      }

      /**
       * If no ratio supplied and image has width / height ratio in object.
       */
      if (!ratio && imageObject.aspect_ratio) {
        return `${(1 / imageObject.aspect_ratio) * 100.00}%`
      }

      let height = imageObject.height
      let width = imageObject.width / 100.00

      /**
       * If ratio is passed as aspect ratio.
       */
      if (ratio && ratio.includes(':')) {
        height = ratio.split(':')[1] * 100.00
        width = ratio.split(':')[0]
      }

      return `${height / width}%`
    },

    /**
     * Get fallback src URL.
     * - Can either be src or data-src based on lazyload.
     * @param {Boolean} dataAttribute - Is data- attribute.
     * @returns {String|Null}
     */
    getFallbackSrc(dataAttribute) {
      if (
        (!this.image.src && !this.image.url) ||
        (this.lazyload && !dataAttribute)
      ) {
        return null
      }

      return getSizedImageUrl(this.image.src || this.image.url, '100x')
    },
  },
}
</script>
